import { useEffect } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';

const TREATMENT_VARIANT = 'treatment';

/**
 * Reads the render-ready offers/rewards experiment variant WITHOUT logging an
 * exposure. Exposure is logged separately, and only for eligible users, via
 * {@link useExposeRenderReadyOffersRewardsExperiment}. This keeps the experiment
 * safe by default: if Amplitude is slow or fails, this returns `false` and
 * callers fall back to the legacy path.
 */
export const useRenderReadyOffersRewardsEnabled = () => {
  const { variant } = useExperiments();

  return variant(RunningExperiments.EnableRenderReadyOffersRewards, TREATMENT_VARIANT, {
    skipExposureLogging: true,
  });
};

/**
 * Logs an Amplitude exposure for the render-ready offers/rewards experiment.
 *
 * Pass `shouldExpose = true` only when the user is actually eligible to
 * experience a difference between arms (kill-switch on + unified menu, plus a
 * selected store where relevant). This must be true for BOTH control and
 * treatment so exposure is recorded for the whole eligible population.
 * `logExposure` dedupes per variant, so calling it from multiple surfaces is safe.
 */
export const useExposeRenderReadyOffersRewardsExperiment = (shouldExpose: boolean) => {
  const { logExposure } = useExperiments();

  useEffect(() => {
    if (!shouldExpose) {
      return;
    }

    logExposure(RunningExperiments.EnableRenderReadyOffersRewards);
  }, [shouldExpose, logExposure]);
};
